import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  linkedSignal,
  Output,
  signal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { map } from "rxjs";
import { SegmentConfigType, SegmentEx } from "@front/m19-models";
import { SegmentService } from "@front/m19-services";
import { KeywordTrackerConfig, KeywordTrackingFrequency, Marketplace } from "@front/m19-api-client";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { FormsModule } from "@angular/forms";
import {
  IButtonDirective,
  IIconComponent,
  IInputGroupDirective,
  IInputGroupInput,
  ISwitchButtonComponent,
  ITooltipDirective,
  MODAL_DATA,
  ModalOptions,
  ModalRef,
  ModalService,
} from "@front/m19-ui";
import {
  KeywordSegmentModalComponent,
  KeywordSegmentModalData,
} from "@m19-board/segments/keyword-segment-modal/keyword-segment-modal.component";

enum KeywordSelectionMode {
  Bulk,
  FromKeywordSegment,
}

@Component({
  templateUrl: "./add-keyword-tracking-modal.component.html",
  styleUrls: ["./add-keyword-tracking-modal.component.scss"],
  imports: [
    TranslocoDirective,
    FormsModule,
    ITooltipDirective,
    IButtonDirective,
    IInputGroupDirective,
    IInputGroupInput,
    ISwitchButtonComponent,
    IIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddKeywordTrackingModalComponent {
  modalRef = inject<ModalRef<KeywordTrackerConfig[]>>(ModalRef);
  private segmentService = inject(SegmentService);
  private modalService = inject(ModalService);

  private readonly modalData = inject<AddKeywordTrackingModalData>(MODAL_DATA);
  private readonly translocoService = inject(TranslocoService);

  readonly SelectionModes = KeywordSelectionMode;

  accountId = this.modalData.accountId;
  marketplace = this.modalData.marketplace;
  organizationId = this.modalData.organizationId;

  bulkKeywords = "";
  selectionMode = KeywordSelectionMode.Bulk;
  keywordList: string[] = this.modalData.keywords;
  readonly keywordSegments = toSignal(
    this.segmentService
      .getSegments(this.accountId, this.marketplace)
      .pipe(
        map((segmentsMap: Map<number, SegmentEx>) =>
          Array.from(segmentsMap.values()).filter((seg) => seg.segmentType === SegmentConfigType.KeywordSegment),
        ),
      ),
    { initialValue: [] },
  );
  readonly selectedKeywordSegments = linkedSignal<SegmentEx[], SegmentEx[]>({
    source: this.keywordSegments,
    computation: () => [],
  });
  private readonly keywordSegmentFilter = signal("");
  readonly filteredKeywordSegments = computed(() => {
    const filter = this.keywordSegmentFilter().toLowerCase();
    return this.keywordSegments().filter((seg) => seg.name.toLowerCase().includes(filter));
  });
  private readonly keywordSegmentItems = computed(
    () =>
      new Map(
        this.keywordSegments().map((seg) => [seg.segmentId, seg.items.map((item) => item.targetingValue)] as const),
      ),
  );

  @Output() public save = new EventEmitter<KeywordTrackerConfig[]>();

  addKeyword(): void {
    if (this.keywordToAddInvalid()) {
      return;
    }
    this.keywordList.push(
      ...this.bulkKeywords
        .split("\n")
        .map((x) => x.trim())
        .filter((x) => x !== ""),
    );
    this.bulkKeywords = "";
  }

  keywordToAddInvalid(): boolean {
    return this.bulkKeywords === "";
  }

  deleteKeyword(keyword: string) {
    this.keywordList.splice(this.keywordList.indexOf(keyword), 1);
  }

  canSave() {
    return this.keywordList.length > 0 || this.bulkKeywords !== "";
  }

  onSave(): void {
    const terms: KeywordTrackerConfig[] = [];
    this.keywordList.forEach((keyword) => {
      terms.push({
        accountId: this.accountId,
        marketplace: this.marketplace,
        organizationId: this.organizationId,
        searchTerm: keyword,
        frequency: KeywordTrackingFrequency.daily,
      });
    });
    this.modalRef.close(terms);
  }

  changeKeywordSelectionMode(mode: KeywordSelectionMode) {
    this.selectedKeywordSegments.set([]);
    this.bulkKeywords = "";
    this.selectionMode = mode;
  }

  isKeywordSegmentAddButtonDisabled() {
    return this.selectedKeywordSegments().length === 0;
  }

  selectAllKeywordSegments() {
    const selected = [...this.selectedKeywordSegments()];
    for (const keywordSegment of this.filteredKeywordSegments()) {
      if (!selected.some((seg) => seg.segmentId == keywordSegment.segmentId)) {
        selected.push(keywordSegment);
      }
    }
    this.selectedKeywordSegments.set(selected);
  }

  unselectAllKeywordSegments() {
    this.selectedKeywordSegments.set([]);
  }

  setKeywordSegmentFilter(value: string) {
    this.keywordSegmentFilter.set(value);
  }

  selectKeywordSegment(keywordSegment: SegmentEx) {
    this.selectedKeywordSegments.update((selected) =>
      selected.some((seg) => seg.segmentId === keywordSegment.segmentId)
        ? selected.filter((seg) => seg.segmentId !== keywordSegment.segmentId)
        : [...selected, keywordSegment],
    );
  }

  getKeywordSegmentItems(keywordSegment: SegmentEx) {
    return this.keywordSegmentItems().get(keywordSegment.segmentId) ?? [];
  }

  isKeywordSegmentSelected(keywordSegment: SegmentEx) {
    return this.selectedKeywordSegments().some((seg) => seg.segmentId === keywordSegment.segmentId);
  }

  keywordSegmentAlreadySelected(keywordSegment: SegmentEx) {
    return this.getKeywordSegmentItems(keywordSegment).every((kw) => this.keywordList.includes(kw));
  }

  addKeywordByKeywordSegment() {
    for (const keywordSegment of this.selectedKeywordSegments()) {
      for (const kw of this.getKeywordSegmentItems(keywordSegment)) {
        if (this.keywordList.findIndex((x) => x === kw) < 0) this.keywordList.push(kw);
      }
    }
  }

  openKeywordSegmentInfo(keywordSegment: SegmentEx) {
    const modalOptions: ModalOptions<KeywordSegmentModalData> = {
      data: {
        accountId: this.accountId,
        marketplace: this.marketplace,
        isReadOnly: true,
        segment: keywordSegment,
        initSegmentItems: keywordSegment.items,
      },
      modalTitle: this.translocoService.translate("keyword-segment-modal.edit_keyword_segment"),
    };
    this.modalService.openModal(KeywordSegmentModalComponent, modalOptions);
  }
}

export interface AddKeywordTrackingModalData {
  accountId: string;
  marketplace: Marketplace;
  organizationId: number;
  keywords: string[];
}
